// Shared date rendering.
//
// The backend serializes datetimes as naive ISO strings without a timezone
// designator ("2026-09-25T12:00:00.123"), and every stored value is UTC. Per
// ES2015+, `new Date("2026-09-25T12:00:00")` (no offset) is parsed as LOCAL
// time, so a naive UTC string read that way shows 2 h early in summer /
// 1 h in winter — the carrier conversation did exactly that while the ticket
// conversation appended 'Z' (KI-110). Every timestamp goes through here.

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A Date from a backend timestamp: naive strings are UTC. Returns null when
// the value is empty or unparseable.
export function parseUtc(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    let text = String(value).trim();
    if (!text) return null;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)) text = text.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}T/.test(text) && !HAS_OFFSET.test(text)) text = `${text}Z`;
    if (DATE_ONLY.test(text)) text = `${text}T00:00:00Z`;
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DEFAULT_OPTIONS = {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
};

// Date + time in the operators' timezone (all tenants work in France; the
// ticket conversation already renders in Europe/Paris).
export function formatDateTime(value, options = {}, locale = 'fr-FR') {
    const parsed = parseUtc(value);
    if (!parsed) return '—';
    return parsed.toLocaleString(locale, { timeZone: 'Europe/Paris', ...DEFAULT_OPTIONS, ...options });
}

export function formatTime(value, locale = 'fr-FR') {
    return formatDateTime(value, { year: undefined, month: undefined, day: undefined }, locale);
}

// Carrier tracking events (KI-194). A carrier reports the local wall-clock
// time of an event and `date` keeps it as the source wrote it — Ship24 adds a
// fake "Z", Envoi du Net a fake "+00:00", most sources no offset at all — so
// it is NOT a UTC timestamp. The backend adds `date_utc` (true UTC) when the
// source gave a real offset. Same rule as the backend reader
// (app/utils/tracking_time.py): `date_utc` first; else an explicit non-zero
// offset is honoured; else the wall-clock digits are Paris time, shown as
// written, never shifted.
const ISO_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
const FR_WALL_CLOCK = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s*(?:à|a)?\s*(\d{1,2})[:hH](\d{2}))?/;
const REAL_OFFSET = /[+-](\d{2}):?(\d{2})$/;

function wallClockParts(text) {
    let match = ISO_WALL_CLOCK.exec(text);
    if (match) {
        return { year: +match[1], month: +match[2], day: +match[3], hour: match[4], minute: match[5] };
    }
    match = FR_WALL_CLOCK.exec(text);
    if (match) {
        const year = +match[3] < 100 ? 2000 + +match[3] : +match[3];
        return { year, month: +match[2], day: +match[1], hour: match[4], minute: match[5] };
    }
    return null;
}

export function formatTrackingEventDate(event, options = {}, locale = 'fr-FR') {
    if (!event) return '—';
    if (event.date_utc) {
        const instant = parseUtc(event.date_utc);
        if (instant) return formatDateTime(instant, options, locale);
    }
    const raw = event.date;
    if (raw === null || raw === undefined || raw === '') return '—';
    const text = String(raw).trim();
    const offset = REAL_OFFSET.exec(text);
    if (offset && /T\d{2}:\d{2}/.test(text) && (+offset[1] !== 0 || +offset[2] !== 0)) {
        const instant = parseUtc(text);
        if (instant) return formatDateTime(instant, options, locale);
    }
    const parts = wallClockParts(text);
    if (!parts) return text;
    const hasTime = parts.hour !== undefined;
    // The wall-clock digits built and rendered in UTC: shown exactly as written.
    const wall = new Date(Date.UTC(parts.year, parts.month - 1, parts.day,
        hasTime ? +parts.hour : 0, hasTime ? +parts.minute : 0));
    if (Number.isNaN(wall.getTime())) return text;
    const base = hasTime ? DEFAULT_OPTIONS : { year: 'numeric', month: '2-digit', day: '2-digit' };
    return wall.toLocaleString(locale, { ...base, ...options, timeZone: 'UTC' });
}
